import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Trash2, 
  RefreshCw,
  MessageSquare,
  User,
  Calendar,
  MapPin,
  ExternalLink,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const CommentModeration = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchComments();
  }, [page, searchQuery]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', '50');
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`/api/admin/comments?${params}`);
      setComments(response.data.comments);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (monumentId, commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      return;
    }

    setActionLoading(prev => ({ ...prev, [commentId]: true }));
    
    try {
      await axios.delete(`/api/admin/comments/${monumentId}/${commentId}`);
      setComments(prev => prev.filter(comment => comment._id !== commentId));
      toast.success('Comment deleted successfully');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment');
    } finally {
      setActionLoading(prev => ({ ...prev, [commentId]: false }));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-serif font-bold text-text-primary">
          Comment Moderation
        </h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search comments..."
              className="pl-10 pr-4 py-2 glass-morphism rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
            />
          </div>
          <button
            onClick={fetchComments}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-20">
          <MessageSquare className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <h3 className="text-xl font-serif font-semibold text-text-primary mb-2">
            No comments found
          </h3>
          <p className="text-text-secondary">
            {searchQuery ? 'Try adjusting your search terms' : 'No comments have been posted yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment, index) => (
            <motion.div
              key={comment._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="glass-card p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Comment Header */}
                  <div className="flex items-center space-x-4 mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-accent-primary/20 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-accent-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-text-primary">
                          {comment.user.username}
                        </p>
                        <p className="text-xs text-text-muted">
                          User ID: {comment.user._id.slice(-8)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-text-secondary">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(comment.timestamp)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Monument Info */}
                  <div className="flex items-center space-x-2 mb-3">
                    <MapPin className="w-4 h-4 text-text-muted" />
                    <span className="text-sm text-text-secondary">
                      Posted on:
                    </span>
                    <a
                      href={`/story/${comment.monument.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-accent-primary hover:text-accent-secondary transition-colors flex items-center space-x-1"
                    >
                      <span>{comment.monument.title}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                  {/* Comment Content */}
                  <div className="bg-glass-bg/30 rounded-lg p-4 mb-4">
                    <p className="text-text-primary leading-relaxed">
                      {comment.text}
                    </p>
                  </div>

                  {/* Comment Actions */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-text-muted">
                      Comment ID: {comment._id.slice(-8)}
                    </div>
                    <button
                      onClick={() => handleDeleteComment(comment.monument._id, comment._id)}
                      disabled={actionLoading[comment._id]}
                      className="flex items-center space-x-2 px-3 py-1.5 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {actionLoading[comment._id] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-center space-x-2 mt-8">
              <button
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page === 1}
                className="p-2 glass-morphism rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-text-secondary px-4">
                Page {page} of {pagination.pages}
              </span>
              <button
                onClick={() => setPage(prev => Math.min(pagination.pages, prev + 1))}
                disabled={page === pagination.pages}
                className="p-2 glass-morphism rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentModeration;
